import React, { FC } from "react";
import { FeeEarnerAndRatesCascaderContainer } from "../fee-earner-and-rates-cascader-container";
import {
  TeamBuilder,
  TeamMembership,
  PricingLevel,
  TeamMembershipIds,
  Phase,
} from "~/design-system";
import { TeamMembershipPartsFragment } from "~/gql/graphql";
import styles from "./team-builder-container.module.css";

const { ["container"]: containerClassName, ["body-section"]: bodySection } = styles as any;

type TeamBuilderContainerProps = {
  matterId: number;
  externalMatterId?: number | null;
  clientId?: number | null;
  organisationalUnitId?: number;
  teamRates: TeamMembershipPartsFragment[];
  phases: Phase[];
  assignedRateIds: number[];
  currency: { symbol: string; code: string };
  onUpdateMatterTeam: (data: {
    teamMembersToAdd: TeamMembershipIds[];
    teamMembersToRemove: TeamMembershipIds[];
  }) => void;
  onAssignTeamToPhases: (teamMembers: TeamMembership[], phases: Phase[], message?: string) => void;
  assignWorksLoading: boolean;
  findMatterLoading: boolean;
  onDeleteTeamMember: (rateId: number, feeEarnerId: number | null) => void;
  pricingLevel: PricingLevel;
};

export const TeamBuilderContainer: FC<TeamBuilderContainerProps> = ({
  matterId,
  externalMatterId,
  clientId,
  organisationalUnitId,
  teamRates,
  phases,
  assignedRateIds,
  currency,
  onUpdateMatterTeam,
  onAssignTeamToPhases,
  findMatterLoading,
  assignWorksLoading,
  onDeleteTeamMember,
  pricingLevel,
}) => {
  const sortById = (a: { id: number }, b: { id: number }) => b.id - a.id;
  const sortByOrder = (a: { order: number }, b: { order: number }) => a.order - b.order;

  const sortedPhases = (phases || [])
    .map((p) => ({
      ...p,
      autoFocus: p?.autoFocus || false,
      work: [...(p.work || [])].sort(sortById),
      tasks: [...(p.tasks || [])].sort((a, b) => (a?.order || 0) - (b?.order || 0)),
    }))
    .sort(sortByOrder);

  return (
    <div className={containerClassName}>
      <div className={bodySection}>
        <TeamBuilder
          teamRates={teamRates}
          phases={sortedPhases}
          assignedRateIds={assignedRateIds}
          feeEarnerCascadeRender={(values: any) => (
            <FeeEarnerAndRatesCascaderContainer
              matterId={matterId}
              clientId={clientId}
              externalMatterId={externalMatterId}
              organisationalUnitId={organisationalUnitId}
              selectedFeeEarnerLevelId={values.selectedFeeEarnerLevelId}
              selectedFeeEarnerId={values.selectedFeeEarnerId}
              onChange={values.onChange}
              onClear={values.onClear}
              currency={currency}
            />
          )}
          currencySymbol={currency.symbol}
          handleUpdateMatterTeam={async (data) => {
            data.teamMembersToAdd.forEach((tm) => {
              onDeleteTeamMember(tm.rateId, tm.feeEarnerId);
            });
            onUpdateMatterTeam(data);
          }}
          allowAssignToPhases={pricingLevel === PricingLevel.FeeEarner}
          onAssignTeamToPhases={onAssignTeamToPhases}
          assignWorksLoading={assignWorksLoading}
          findMatterLoading={findMatterLoading}
        />
      </div>
    </div>
  );
};
